import type { Page, Pageable } from '../../../core/api/spring-types';
import { normalizePage } from '../../../core/api/spring-types';
import {
    getSchoolsReport,
    getSchoolById,
    getOwnSchool,
    getDefaultSchool,
    createSchool,
    updateSchool,
    deleteSchool,
    getSchoolsStatusReport,
    getSchoolsImportData,
    getSchoolsDropdown,
    getCountiesList,
    getSchoolRegistrationStatus,
} from '../../../api/generated/sdk.gen';
import type {
    SchoolDto,
    SchoolReportDto,
    SchoolDataImportDto,
    SchoolDropdownDto,
    EditionReportDto,
    SchoolRegistrationStatusDto,
} from '../../../api/generated/types.gen';
import { handleResponse } from '@/core/api/response-handler';

/**
 * Schools API - Thin wrapper for auto-generated SDK functions
 * Maintains backward compatibility with existing hook signatures
 */
class SchoolsAPI {
    // Report endpoints
    async getReport(
        filter: Partial<SchoolReportDto>,
        pageable: Pageable
    ): Promise<Page<SchoolReportDto>> {
        const response = await getSchoolsReport({
            query: { ...filter, ...pageable } as any,
        });
        return normalizePage(handleResponse(response));
    }

    /**
     * Pre-check pentru formularul de inscriere: e scoala deja inscrisa in editia curenta?
     * Raspunsul contine DOAR numele profesorului responsabil, niciodata emailul.
     */
    async getRegistrationStatus(codSiiirUnitate: string): Promise<SchoolRegistrationStatusDto> {
        const response = await getSchoolRegistrationStatus({
            query: { codSiiirUnitate },
        });
        return handleResponse(response);
    }

    async getStatusReport(
        filter: Partial<EditionReportDto>,
        pageable: Pageable
    ): Promise<Page<EditionReportDto>> {
        const response = await getSchoolsStatusReport({
            query: { ...filter, ...pageable } as any,
        });
        return normalizePage(handleResponse(response));
    }

    /**
     * Get school profile with nested professor data (including protocol PDFs)
     * Used for preview dialogs where we need professor.protocolDeColaborare
     */
    async getSchoolProfile(schoolId: string): Promise<SchoolReportDto> {
        const response = await getSchoolsReport({
            query: { id: schoolId, page: 0, size: 1 } as any,
        });
        const page = normalizePage<SchoolReportDto>(handleResponse(response));

        if (!page.content || page.content.length === 0) {
            throw new Error('School not found');
        }

        return page.content[0];
    }

    // CRUD operations
    async getById(id: string): Promise<SchoolDto> {
        const response = await getSchoolById({
            path: { id },
        });
        return handleResponse(response);
    }

    async getOwn(): Promise<SchoolDto> {
        const response = await getOwnSchool();
        return handleResponse(response);
    }

    async getDefault(): Promise<SchoolDto> {
        const response = await getDefaultSchool();
        return handleResponse(response);
    }

    async create(data: SchoolDto): Promise<SchoolDto> {
        const response = await createSchool({
            body: data,
        });
        return handleResponse(response);
    }

    async update(data: SchoolDto): Promise<SchoolDto> {
        const response = await updateSchool({
            body: data,
        });
        return handleResponse(response);
    }

    async delete(id: string): Promise<void> {
        const response = await deleteSchool({
            path: { id },
        });
        handleResponse(response);
    }

    // Special endpoints
    async getImportData(
        filter: Partial<SchoolDataImportDto>,
        pageable: Pageable
    ): Promise<Page<SchoolDataImportDto>> {
        const response = await getSchoolsImportData({
            query: { ...filter, ...pageable } as any,
        });
        return normalizePage(handleResponse(response));
    }

    async getDropdown(): Promise<SchoolDropdownDto[]> {
        const response = await getSchoolsDropdown();
        return handleResponse(response);
    }

    async getCounties(): Promise<string[]> {
        const response = await getCountiesList();
        return handleResponse(response);
    }
}

export const schoolsApi = new SchoolsAPI();

// Re-export types for convenience
export type {
    SchoolDto,
    SchoolReportDto,
    SchoolDataImportDto,
    SchoolDropdownDto,
    EditionReportDto,
};
